import { useEffect, useState } from "react";
import { FileUp, Send, CheckCircle2, Clock, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, ts } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const AckBadge = ({ r }) => {
  if (r.doc_type === "997") return <span className="text-slate-500" data-testid={`as2-test-ack-${r.control}`}>No 997 expected</span>;
  if (r.status !== "sent") return <span className="text-slate-500" data-testid={`as2-test-ack-${r.control}`}>Not sent - no 997</span>;
  if (!r.ack_status) return <span className="inline-flex items-center gap-1 text-amber-300" data-testid={`as2-test-ack-${r.control}`}><Clock className="h-3.5 w-3.5" /> Awaiting Group O 997</span>;
  const ok = ["accepted", "accepted_errors"].includes(r.ack_status);
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${ok ? "text-[#22C55E]" : "text-rose-400"}`} data-testid={`as2-test-ack-${r.control}`}>
      <Icon className="h-4 w-4" /> 997 {r.ack_status.replace("_", " ")} {ts(r.ack_at)}
    </span>
  );
};

const Result = ({ r }) => (
  <div className="space-y-1 rounded-sm border border-slate-800 bg-[#252A35] p-3 text-xs" data-testid="as2-test-result">
    <div className="flex flex-wrap items-center gap-2">
      <StatusBadge status={r.status} />
      <span className="font-mono text-amber-300">{r.doc_type} #{r.control}</span>
      <span className="text-slate-400">{(r.summary || "").replace(/^AS2 connectivity test /, "").replace(/ sent manually by .*/, "")}</span>
      {r.sent_at && <span className="text-slate-500">{ts(r.sent_at)}</span>}
      <span className="ml-auto"><AckBadge r={r} /></span>
    </div>
    {r.as2 && <div className="font-mono text-slate-300" data-testid="as2-test-mdn">HTTP {r.as2.http_status} · MDN: {r.as2.mdn_status || "none"} {r.as2.mdn_detail} · Message-ID {r.as2.message_id}</div>}
    {r.error && <div className="text-rose-300" data-testid="as2-test-error">{r.error}</div>}
  </div>
);

export const As2TestSendDialog = ({ open, onOpenChange, onDone }) => {
  const [raw, setRaw] = useState("");
  const [name, setName] = useState("");
  const [readdress, setReaddress] = useState(true);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [partner, setPartner] = useState("GROUPO");
  const [prod, setProd] = useState(false);
  const [plist, setPlist] = useState([]);
  const loadRecent = () => api.get("/as2/test-recent").then((r) => setRecent(r.data)).catch(() => {});
  useEffect(() => {
    if (!open) return undefined;
    setRaw(""); setName(""); setReaddress(true);
    loadRecent();
    api.get("/partners").then((r) => setPlist(r.data.partners.filter((p) => p.active))).catch(() => {});
    const t = setInterval(loadRecent, 15000);
    return () => clearInterval(t);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const sendPack = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/as2/send-test-pack?partner=${partner}&production=${prod}`)).data;
      r.every((x) => x.status === "sent") && r.length === 3 ? toast.success("Test pack sent - 3 MDNs received") : toast.error("Test pack had failures - see details");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      loadRecent();
    }
  };
  const sample = async () => {
    const r = (await api.get("/as2/test-sample-997", { params: { partner, production: prod } })).data;
    setRaw(r.raw);
    setName(r.filename);
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setName(f.name); f.text().then(setRaw); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/as2/send-test", { raw, readdress, filename: name, partner, production: prod })).data;
      r.status === "sent" ? toast.success("Sent - MDN received from Group O") : toast.error("Test send failed - see details");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      loadRecent();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-slate-800 bg-[#1F232C] text-slate-100" data-testid="as2-test-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Send test file to Group O</DialogTitle>
          <DialogDescription className="text-slate-400">
            Sends one X12 file to Group O over AS2 straight away (even in test mode) so they can confirm receipt. The synchronous MDN result is shown below and the file is logged as a test message.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-xs text-slate-300">Send to
          <select value={partner} onChange={(e) => setPartner(e.target.value)} className="h-8 rounded-md border border-slate-700 bg-[#111317] px-2 text-xs text-slate-100" data-testid="as2-test-partner">
            {plist.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </label>
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="as2-test-mode">
          <span className="text-slate-300">Envelope IDs</span>
          {[[false, "Test IDs (ISA15 = T)"], [true, "Production IDs"]].map(([v, lb]) => (
            <button key={lb} type="button" onClick={() => setProd(v)} className={`rounded-sm border px-2.5 py-1 transition-colors ${prod === v ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`} data-testid={`as2-test-mode-${v ? "prod" : "test"}`}>{lb}</button>
          ))}
          {prod && <span className="text-amber-300" data-testid="as2-test-prod-warning">Sent with your production IDs to the partner's production ID and your ISA15 usage indicator. Ask the partner to expect these - the 940s are marked DO NOT SHIP.</span>}
        </div>
        <div className="space-y-2 rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="as2-test-pack">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={sendPack} disabled={busy} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="as2-test-pack-btn"><Send className="mr-1.5 h-4 w-4" /> {busy ? "Sending…" : "Send test pack"}</Button>
            <span className="text-xs text-slate-300">One click sends, in order: 940 customer order, 940 raw material, 997 - addressed with the envelope IDs chosen above and marked "AS2 CONNECTIVITY TEST - DO NOT SHIP".</span>
          </div>
        </div>
        <div className="space-y-2" data-testid="as2-test-recent">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
            Recent test sends - Group O 997 status (checks every 15 seconds)
            <Button size="sm" variant="ghost" onClick={loadRecent} className="ml-auto h-7 text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="as2-test-refresh-btn"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Check now</Button>
          </div>
          <div className="max-h-56 space-y-2 overflow-auto">
            {recent.length ? recent.map((r) => <Result key={r.id} r={r} />) : <div className="text-xs text-slate-500" data-testid="as2-test-recent-empty">No test files sent yet.</div>}
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Or send a single file</div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-slate-700 px-3 py-1.5 text-xs text-slate-200 transition-colors hover:bg-slate-800" data-testid="as2-test-file-label">
            <FileUp className="h-3.5 w-3.5" /> Upload file
            <input type="file" accept=".txt,.edi,.x12,.dat" className="hidden" onChange={onFile} data-testid="as2-test-file-input" />
          </label>
          <Button variant="ghost" size="sm" onClick={sample} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="as2-test-sample-997">Load test 997 (EP to partner)</Button>
          {name && <span className="font-mono text-xs text-slate-500">{name}</span>}
        </div>
        <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="ISA*00*...~" className="h-32 resize-none border-slate-800 bg-[#111317] font-mono text-xs text-emerald-200" data-testid="as2-test-textarea" />
        <label className="flex items-start gap-3 text-xs text-slate-300">
          <Switch checked={readdress} onCheckedChange={setReaddress} data-testid="as2-test-readdress" />
          <span>Re-address envelope from EP to the partner's <b>{prod ? "production" : "test"}</b> IDs{prod ? "" : " (ISA15 = T)"}. Leave on when sending a sample file that was addressed the other way.</span>
        </label>
        <div className="flex justify-end">
          <Button onClick={send} disabled={!raw.trim() || busy} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="as2-test-send-btn">
            <Send className="mr-1.5 h-4 w-4" /> {busy ? "Sending…" : "Send to Group O"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
